import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link as MuiLink
} from '@mui/material';
import { Link } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';

const ForgotPasswordPage = () => {
  // Estados
  const [identificador, setIdentificador] = useState('');
  const [emailNovo, setEmailNovo] = useState('');
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Hook de autenticação
  const { requestPasswordReset, adicionarEmailRecuperacao } = useAuth();

  // Função para solicitar reset de senha
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const result = await requestPasswordReset({
        identificador
      });
      setMessage(result.mensagem);
    } catch (err) {
      console.log('Detalhes do erro:', err.response?.data);

      if (err.response?.data?.requiresEmail) {
        setShowEmailDialog(true);
        setError(err.response.data.mensagem);
      } else {
        setError(err.message || 'Erro ao solicitar reset de senha');
      }
    } finally {
      setLoading(false);
    }
  };

  // Função para adicionar email
  const handleAdicionarEmail = async () => {
    if (!emailNovo) {
      setError('Por favor, insira um email válido');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await adicionarEmailRecuperacao({
        identificador,
        email: emailNovo
      });

      setShowEmailDialog(false);
      setMessage(result.mensagem || 'Email adicionado com sucesso. Verifique sua caixa de entrada.');
      setEmailNovo('');
    } catch (err) {
      setError(err.response?.data?.mensagem || 'Erro ao adicionar email');
    } finally {
      setLoading(false);
    }
  };

  // Função para fechar o diálogo
  const handleCloseDialog = () => {
    if (!loading) {
      setShowEmailDialog(false);
      setEmailNovo('');
      setError('');
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5" gutterBottom>
          Recuperar Senha
        </Typography>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Digite seu email ou username cadastrado para receber as instruções de recuperação de senha.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {message && (
          <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
            {message}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Email ou Username"
            name="identificador"
            autoComplete="email username"
            autoFocus
            value={identificador}
            onChange={(e) => setIdentificador(e.target.value)}
            disabled={loading}
            placeholder="Digite seu email ou username cadastrado"
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading || !identificador}
          >
            {loading ? <CircularProgress size={24} /> : 'Recuperar Senha'}
          </Button>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <MuiLink component={Link} to="/login" variant="body2">
              Voltar para o Login
            </MuiLink>
          </Box>
        </Box>

        {/* Diálogo para adicionar email */}
        <Dialog
          open={showEmailDialog}
          onClose={handleCloseDialog}
          aria-labelledby="add-email-dialog-title"
          aria-describedby="add-email-dialog-description"
        >
          <DialogTitle id="add-email-dialog-title">
            Adicionar Email para Recuperação
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="add-email-dialog-description" sx={{ mb: 2 }}>
              Para recuperar sua senha, precisamos de um email válido onde enviaremos o link de recuperação.
              Este email será associado à sua conta para futuras recuperações de senha.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              value={emailNovo}
              onChange={(e) => setEmailNovo(e.target.value)}
              disabled={loading}
              error={!!error}
              helperText={error}
              placeholder="Digite um email válido"
            />
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseDialog} 
              disabled={loading}
              color="inherit"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleAdicionarEmail} 
              disabled={loading || !emailNovo}
              variant="contained"
            >
              {loading ? <CircularProgress size={24} /> : 'Adicionar Email'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default ForgotPasswordPage;